import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtINR, fmtDate, bucketBadge } from "../lib/format";
import { Plus, ArrowLeft, MapPin } from "lucide-react";

export default function CustomerDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/customers/${id}/overview`);
    setData(data);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-slate-400">Loading…</div>;
  const { customer, summary, sites, invoices, recharges } = data;

  const KV = ({ k, v }) => (<div><div className="text-[11px] uppercase tracking-wide text-slate-400">{k}</div><div className="text-sm font-semibold">{v}</div></div>);

  return (
    <div className="space-y-5">
      <button onClick={() => nav("/customers")} className="text-sm text-slate-500 flex items-center gap-1 hover:text-slate-700"><ArrowLeft size={15} /> Customers</button>

      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-head text-2xl font-extrabold">{customer.name}</h1>
            <div className="flex gap-6 mt-3 flex-wrap">
              <KV k="Customer code" v={customer.code || "—"} />
              <KV k="Account manager" v={customer.account_manager || "—"} />
              <KV k="Sites" v={summary.sites} />
              <KV k="Billable items" v={summary.billable_items} />
              <KV k="Annual value (ARC)" v={fmtINR(summary.annual_value_arc)} />
            </div>
          </div>
          {can("create") && <button onClick={() => nav("/customer-link")} className="h-9 px-4 rounded-lg bg-[var(--brand)] text-white text-sm font-semibold flex items-center gap-2"><Plus size={16} /> Add Customer Link</button>}
        </div>
      </div>

      {sites.map((site, si) => (
        <div key={si} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="bg-slate-50 px-5 py-3 border-b flex items-center gap-2 text-sm font-semibold">
            <MapPin size={15} className="text-slate-400" /> {site.site_name}{site.city ? ` · ${site.city}` : ""}
          </div>
          {site.connections.map((conn) => (
            <div key={conn.id} className="px-2 py-2">
              <div className="flex items-center gap-2 px-3 py-2 text-sm">
                <span className="font-mono-num text-xs text-slate-500">{conn.internal_connection_id}</span>
                <span className="text-slate-700">{conn.delivery_type}</span>
                {conn.pop_name && <span className="text-xs text-slate-400">· POP: {conn.pop_name}</span>}
                {!conn.data_complete && <span className="text-[11px] px-2 py-0.5 rounded bg-amber-100 text-amber-700" title={(conn.data_quality_flags || []).join(", ")}>incomplete</span>}
                {can("edit") && <button onClick={() => nav(`/customer-link/${conn.id}`)} data-testid={`edit-link-${conn.id}`} className="ml-auto text-xs font-semibold text-blue-700 hover:underline">Edit link</button>}
              </div>
              {conn.billable_items.length > 0 && (
                <div className="overflow-x-auto px-1">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-[10px] uppercase text-slate-400">
                      <th className="px-3 py-1.5">Item</th><th className="px-3 py-1.5">Vendor</th><th className="px-3 py-1.5">Type</th><th className="px-3 py-1.5">Bandwidth</th><th className="px-3 py-1.5">Billing</th><th className="px-3 py-1.5">ARC</th><th className="px-3 py-1.5">Expiry / due</th><th className="px-3 py-1.5">Status</th>
                    </tr></thead>
                    <tbody>
                      {conn.billable_items.map((it) => (
                        <tr key={it.id} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" onClick={() => nav(`/circuit/${it.id}`)}>
                          <td className="px-3 py-2 font-mono-num text-xs">{it.internal_billable_id}</td>
                          <td className="px-3 py-2">{it.vendor_name || "—"}</td>
                          <td className="px-3 py-2">{it.item_type}</td>
                          <td className="px-3 py-2">{it.bandwidth || "—"}</td>
                          <td className="px-3 py-2">{it.billing_type}</td>
                          <td className="px-3 py-2 font-mono-num">{fmtINR(it.arc)}</td>
                          <td className="px-3 py-2 font-mono-num">{fmtDate(it.next_action_date)}</td>
                          <td className="px-3 py-2">{it.bucket && <span className={`text-[10px] px-2 py-0.5 rounded ${bucketBadge(it.bucket)}`}>{it.bucket}</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      ))}

      <div className="grid md:grid-cols-2 gap-5">
        <Panel title={`Invoices (${invoices.length})`}>
          {invoices.length === 0 ? <Empty>No invoices.</Empty> : invoices.slice(0, 20).map((inv) => (
            <Line key={inv.id} left={inv.invoice_number || inv.period || "—"} right={fmtINR(inv.invoice_amount || inv.expected_amount)} sub={inv.paid ? "Paid" : "Pending"} />
          ))}
        </Panel>
        <Panel title={`Recharges & payments (${recharges.length})`}>
          {recharges.length === 0 ? <Empty>No history yet.</Empty> : recharges.slice(0, 20).map((r) => (
            <Line key={r.id} left={fmtDate(r.recharge_date)} right={fmtINR(r.amount)} sub={r.was_late ? `${r.days_late}d late` : (r.type || "recharge")} />
          ))}
        </Panel>
      </div>
    </div>
  );
}

const Panel = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
    <div className="bg-slate-50 px-5 py-3 border-b text-sm font-semibold">{title}</div>
    <div className="divide-y divide-slate-100">{children}</div>
  </div>
);
const Line = ({ left, right, sub }) => (
  <div className="px-5 py-2.5 flex items-center justify-between text-sm">
    <div><div className="font-medium">{left}</div><div className="text-xs text-slate-400">{sub}</div></div>
    <div className="font-mono-num">{right}</div>
  </div>
);
const Empty = ({ children }) => <div className="px-5 py-8 text-center text-slate-400 text-sm">{children}</div>;
